import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = `${process.env.PUBLIC_URL}/prasha-logo.jpeg`;

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'signup' ? 'signup' : 'login';
  const [tab, setTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [signupOtp, setSignupOtp] = useState('');
  const { login: authLogin, user } = useAuth();
  const navigate = useNavigate();

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '', role: 'student' });

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      authLogin(res.data.token, res.data.user);
      toast.success('Welcome back!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (signupForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup/request-otp`, signupForm);
      setOtpSent(true);
      toast.success(res.data.message || 'Verification code sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!signupOtp.trim()) { toast.error('Enter the OTP sent to your email'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup/verify-otp`, {
        email: signupForm.email,
        otp: signupOtp
      });
      authLogin(res.data.token, res.data.user);
      toast.success('Account created successfully!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="page-container min-h-screen flex items-center justify-center py-12 px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="PRASHA CoRe" className="h-14 w-14 mx-auto mb-3 rounded-lg object-contain" />
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Welcome to PRASHA CoRe
          </h1>
          <p className="text-sm text-slate-500 mt-1">Medical Research Platform</p>
        </div>

        <div className="bg-white border border-slate-100 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Log In</TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-sm font-medium text-slate-700 mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="login-email" type="email" placeholder="you@university.edu" required value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="pl-10 h-11 bg-slate-50/50" data-testid="login-email-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-sm font-medium text-slate-700 mb-1.5 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="login-password" type={showPassword ? 'text' : 'password'} placeholder="Enter password" required
                      value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="pl-10 pr-10 h-11 bg-slate-50/50" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#1a5c5c] hover:bg-[#154a4a] text-white h-11 btn-teal-glow" data-testid="login-submit-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Log In
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={otpSent ? handleVerifyOtp : handleRequestOtp} className="space-y-4">
                <div>
                  <Label htmlFor="signup-name" className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="signup-name" placeholder="Dr. John Smith" required value={signupForm.name}
                      onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                      disabled={otpSent}
                      className="pl-10 h-11 bg-slate-50/50" data-testid="signup-name-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="signup-email" className="text-sm font-medium text-slate-700 mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="signup-email" type="email" placeholder="you@university.edu" required value={signupForm.email}
                      onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                      disabled={otpSent}
                      className="pl-10 h-11 bg-slate-50/50" data-testid="signup-email-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="signup-password" className="text-sm font-medium text-slate-700 mb-1.5 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="signup-password" type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" required
                      value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                      disabled={otpSent}
                      className="pl-10 pr-10 h-11 bg-slate-50/50" data-testid="signup-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">I am a</Label>
                  <Select value={signupForm.role} onValueChange={(v) => setSignupForm({ ...signupForm, role: v })} disabled={otpSent}>
                    <SelectTrigger className="h-11 bg-slate-50/50" data-testid="signup-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Medical Student</SelectItem>
                      <SelectItem value="mentor">Research Mentor</SelectItem>
                      <SelectItem value="university">University / Institution</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {otpSent ? (
                  <>
                    <div>
                      <Label htmlFor="signup-otp" className="text-sm font-medium text-slate-700 mb-1.5 block">Email OTP</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="signup-otp"
                          inputMode="numeric"
                          placeholder="Enter 6-digit OTP"
                          required
                          value={signupOtp}
                          onChange={(e) => setSignupOtp(e.target.value)}
                          className="pl-10 h-11 bg-slate-50/50"
                          data-testid="signup-otp-input"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        We sent a verification code to {signupForm.email}.
                      </p>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-[#1a5c5c] hover:bg-[#154a4a] text-white h-11 btn-teal-glow" data-testid="signup-submit-btn">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Verify OTP & Create Account
                    </Button>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1 h-11 border-slate-200" disabled={loading} onClick={() => { setOtpSent(false); setSignupOtp(''); }}>
                        Edit Details
                      </Button>
                      <Button type="button" variant="outline" className="flex-1 h-11 border-slate-200" disabled={loading} onClick={handleRequestOtp}>
                        Resend OTP
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button type="submit" disabled={loading} className="w-full bg-[#1a5c5c] hover:bg-[#154a4a] text-white h-11 btn-teal-glow" data-testid="signup-submit-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Send OTP
                  </Button>
                )}
              </form>
            </TabsContent>
          </Tabs>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-400">or continue with</span></div>
          </div>

          {/* Google Login */}
          <Button variant="outline" className="w-full h-11 border-slate-200 hover:bg-slate-50 text-slate-700" onClick={handleGoogleLogin} data-testid="google-login-btn">
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <p className="text-xs text-slate-400 text-center mt-4">
            By signing up, you agree to our Terms & Conditions and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}
